import { Box, Button, Grid, SelectChangeEvent, Typography, TextField, Divider, Container, Paper } from '@mui/material';
import React, { FC, useEffect, useState, useMemo, useCallback } from 'react';
import { useAppDispatch, useAppSelector, } from "../../store/hooks";
import CustomTextField from '../../components/FormFields/CustomTextField';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import LoadingButton from '@mui/lab/LoadingButton';
import moment from 'moment';
import { Utils, VocuherTypes } from "../../utils/common-utils";
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  GridValueGetterParams,
  GridColDef,
  GridActionsCellItem,
  GridSelectionModel,
} from "@mui/x-data-grid";

import CustomDataGrid from '../../components/DataGrid/DataGrid';

//Dropdown imports
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';

import { ApiStatus } from "../../utils/api.types";
import { fetchInvoices, manualPayment } from '../../store/invoiceStore/invoiceThunk';
import CustomSelect, { IOptions } from '../../components/FormFields/CustomSelect';
import { PaymentReceipt, PaymentReceiptApplication, SettleInvoicePayment } from '../../utils/invoice.types';
import { attachInvoiceToPayment } from '../../store/CashInflowStore/cashInflowThunk';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { AInvoice } from '../../utils/invoice.types';
import { fetchTaxRates } from '../../store/gstStore/gstThunk';
import { toast } from 'react-toastify';

const PaymentApplication: FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  
  // Get parameters from URL
  const paymentId = searchParams.get('paymentId');
  const isInflow = searchParams.get('type') === 'inflow';
  const selectedBuyerOrSupplierId = searchParams.get('buyerId');
  
  // Sample data - in real app, fetch based on paymentId
  const data = {
    payment_id: paymentId,
    total_unapplied: searchParams.get('amount') || '0',
    buyer: searchParams.get('buyerName') || '',
    payment_date: searchParams.get('date') || new Date().toISOString(),
    payment_amount: searchParams.get('amount') || '0'
  };

  const { invoices, status } = useAppSelector(state => state.invoices);
  const { selectedAccount } = useAppSelector(state => state.auth);

  const [showError, setShowError] = useState<Boolean>(false);
  const [showErrorMessage, setShowErrorMessage] = useState<String>('');
  const [showErrorDesc, setShowErrorDesc] = useState<String>('');

  const [selectedAmountRange, setSelectedAmountRange] = useState('0 - 99999');
  const [selectedInvoiceStatus, setSelectedInvoiceStatus] = useState('PENDING');
  const [startDate, setStartDate] = useState<Date>(new Date(Date.now() - 60 * 24 * 60 * 60 * 1000));
  const [endDate, setEndDate] = useState<Date>(new Date());
  
  const [fetchedInvoices, setFetchedInvoices] = useState<AInvoice[]>([]);
  const [selectedRows, setSelectedRows] = useState<any>([]);
  const [selectedInvoicesUpdated, setSelectedInvoicesUpdated] = useState<AInvoice[]>([]);
  
  const [amountRecevied, setAmountRecevied] = useState<Number>(0);
  const [totalPaymentReceiptAmount, setTotalPaymentReceiptAmount] = useState<Number>(Number(data.total_unapplied));
  const [totalUnappliedAmount, setTotalUnappliedAmount] = useState<Number>(Number(data.total_unapplied));
  const [totalAppliedAmount, setTotalAappliedAmount] = useState<Number>(0);

  const [taxRateOptions, setTaxRateOptions] = useState<any>();
  const [tdsTaxRateOptions, setTdsTaxRateOptions] = useState<any[]>([]);
  const [tcsTaxRateOptions, setTcsTaxRateOptions] = useState<IOptions[]>([]);

  const [invoiceStatusOptions, setInvoiceStatusOptions] = useState<IOptions[]>([
    { label: 'Paid', value: 'PAID' },
    { label: 'Partially Paid', value: 'PARTIALLY_PAID' },
    { label: 'Pending', value: 'PENDING' },
    { label: 'Invoice Sent', value: 'INVOICE_SENT' }
  ]);

  const [amountRangeOptions, setAmountRangeOptions] = useState<IOptions[]>([
    { label: 'Below 1 Lakh', value: '0 - 99999' },
    { label: '1,00,000 - 3,00,000', value: '100000 - 300000' },
    { label: '3,00,001 - 5,00,000', value: '300001 - 500000' },
    { label: '5,00,001 - 7,00,000', value: '500001 - 700000' },
    { label: '7,00,001 - 9,00,000', value: '700001 - 900000' },
    { label: '9,00,001 & Above', value: '900001 & Above' }
  ]);

  // Schema to validate payment form
  const schema = yup.object().shape({
    base: yup.string().required("Base Amount is required."),
    tax: yup.string().required("Tax is required."),
    tds: yup.string().required("TDS is required."),
  });

  const methods = useForm<any>({ mode: 'onChange', reValidateMode: 'onChange', resolver: yupResolver(schema) });

  // Memoize selectionModel to ensure stable reference
  const memoizedSelectionModel = useMemo(() => {
    return selectedRows || [];
  }, [selectedRows]);

  useEffect(() => {
    let isMounted = true;
    const getTdsTcsTaxRates = async () => {
      dispatch(fetchTaxRates()).unwrap().then(async (data) => {
        if (data && isMounted) {
          if (data.length > 0) {
            setTaxRateOptions(data)
            let tdsRates = data.filter((items) => items.taxType == "tds");
            let tcsRates = data.filter((items) => items.taxType == "tcs");

            await tdsRates.forEach(async (item: string | any) => {
              setTdsTaxRateOptions((taxOptions: any) => ([...taxOptions, {
                label: `${item.taxRate}% - ${item.taxSection} ${item.taxName}`,
                value: item._id
              }]));
            });

            await tcsRates.forEach(async (item: string | any) => {
              setTcsTaxRateOptions((taxOptions: any) => ([...taxOptions, {
                label: `${item.taxRate}% - ${item.taxSection} ${item.taxName}`,
                value: item._id
              }]));
            });
          }
        }
      });
    }
    if(isMounted) {
      getTdsTcsTaxRates()
    }
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    getInvoices();
  }, [selectedInvoiceStatus, startDate, endDate, selectedAmountRange]);

  useEffect(() => {
    if (fetchedInvoices.length > 0) {
      let totalSum = Number(0);
      const defaultSelected = fetchedInvoices
        .filter((invoice) => Number(invoice.paymentAmount) > 0)
        .map((invoiceN) => { 
          totalSum += Number(invoiceN.paymentAmount);
          return {
            _id: invoiceN._id,
            invoiceNumber: invoiceN.invoiceNumber,
            paymentAmount: invoiceN.paymentAmount, 
            pdfTemplate: invoiceN.pdfTemplate, 
            principalAmount: invoiceN.principalAmount, 
            finalTax: invoiceN.finalTax, 
            tdsSelected: invoiceN.tdsSelected
          }
        });
      
      setTotalAappliedAmount(totalSum)
      // Fix: Set selectedRows to IDs only for DataGrid selectionModel
      setSelectedRows(defaultSelected.map(item => item._id));
      // Set the objects separately for business logic
      setSelectedInvoicesUpdated(defaultSelected as AInvoice[]);
    }
  }, [fetchedInvoices]);

  useEffect(() => {
    if(invoices.length > 0 ) {
      let totalOutstanding = Number(0);
      const sortedInvoices = [...invoices].sort(
        (a: any, b: any) => new Date(a.date).getTime() - new Date(b.date).getTime()
      );

      sortedInvoices.map((a: any) => {
        totalOutstanding += Number(a.balanceAmount);
        return a;
      });

      setAmountRecevied(0);

      let data12 = sortedInvoices.map((row: any) => {
        return { ...row, paymentAmount: 0, tempBalanceAmount: row.balanceAmount };
      });
      setFetchedInvoices(data12);
    } else {
      setFetchedInvoices([])
    }
  }, [invoices]);

  const getInvoices = async () => {
    const { _id: id } = selectedAccount;
    let params = ""
    if (selectedBuyerOrSupplierId) {
      params += "filter=" + selectedBuyerOrSupplierId;
    }

    if (selectedInvoiceStatus) {
      params += "&status=PENDING,PARTIALLY_PAID";
    }
    
    params += isInflow ? `&voucherType=${VocuherTypes.GST_INVOICE}` : `&voucherType=${VocuherTypes.PURCHASE_INVOICE}`
    
    if (isInflow) {
      await dispatch(fetchInvoices({ accountId: id, type: 'sales', params: params }));
    } else {
      await dispatch(fetchInvoices({ accountId: id, type: 'purchase', params: params }));
    }
  };

  const additionalChargesSum = (invoice: any) => {
    let _additionalCharges;
    let initialSum = 0;
    if (invoice.additionalCharges && invoice.additionalCharges.charges && invoice.additionalCharges.charges.length > 0) {
      _additionalCharges = invoice.additionalCharges.charges.reduce((previousValue: any, currentValue: any) => previousValue + Number(currentValue.value), initialSum);
    } else {
      _additionalCharges = 0;
    }
    return _additionalCharges;
  };

  const onSubmit = async (submitData: SettleInvoicePayment) => {
    let paymentsArray: PaymentReceipt[] = []

    if (selectedInvoicesUpdated.length <= 0) {
      toast.error("Please select atleast one invoice to receive the payment");
      return
    }

    selectedInvoicesUpdated.forEach((invoice:any) => {
      var tdsRate = (invoice?.tdsSelected && invoice?.tdsSelected.tdsSelected) ? invoice?.tdsSelected.tdsSelected.rate : undefined;
      var tdsAmount = tdsRate ? Number((tdsRate * Number(invoice.paymentAmount)) / 100) : 0;

      let attachPaymentToInvoice: PaymentReceipt = {
        invoice: invoice._id,
        appliedAmountBase: Number(invoice.paymentAmount) - tdsAmount,
        appliedAmountTax: 0,
        appliedAmountTDS: tdsAmount,
        appliedAmountTotal: Number(invoice.paymentAmount),
        remarks: "PAID",
        activityType: "payment",
        appliedDate: new Date().toISOString()
      };

      if (isInflow) {
        attachPaymentToInvoice["paymentReceipt"] = data.payment_id || '';
      } else {
        attachPaymentToInvoice["vpId"] = data.payment_id || '';
      }
      paymentsArray.push(attachPaymentToInvoice)
    });

    let payLoad: PaymentReceiptApplication = {
      payments: paymentsArray
    };

    if (isInflow) {
      await dispatch(attachInvoiceToPayment(payLoad));
    } else {
      await dispatch(manualPayment(payLoad));
    }
    
    toast.success("Payment applied successfully");
    navigate('/payments');
  };

  const applyNewPyamentAmount = (amount: any) => {
    if(amount == 0 ) {
      setAmountRecevied(0)
    } else {
      setAmountRecevied(Number(Number(amount).toFixed(2)));
    }

    let remainingPayment = Number(amount) || 0;

    let updatedInvoices = fetchedInvoices.map((obj: any) => ({
        ...obj,
        tempBalanceAmount: obj.balanceAmount,
        paymentAmount: 0
    }));

    updatedInvoices = updatedInvoices.map((obj: any) => {
      if (remainingPayment <= 0) return obj;
      const invoiceFinalAmount = obj.amount + additionalChargesSum(obj);
      const balanceAmount = obj.tempBalanceAmount || invoiceFinalAmount;

      const paymentToApply = Math.min(remainingPayment, balanceAmount);
      remainingPayment -= paymentToApply;

      let returnObj = {
        ...obj, 
        paymentAmount : Number(paymentToApply).toFixed(2), 
        tempBalanceAmount : Number(balanceAmount - paymentToApply).toFixed(2) || 0
      };
      return returnObj;
    });

    if (Number(Number(remainingPayment).toFixed(2)) > 0) {
      setShowError(true);
      setShowErrorMessage(`An excess payment of ₹${Number(remainingPayment).toFixed(2)} has been detected.`)
      setShowErrorDesc(`All invoices are already settled. Do you want to record this amount as an advance or unassigned balance?`)
    } else if (selectedRows && selectedRows.length <= 0 ) {
      setShowError(true);
      setShowErrorMessage(`No invoices are selected. Do you want to record this amount as an advance or unassigned balance?`)
      setShowErrorDesc('');
    } else if (remainingPayment == 0 && selectedRows.length > 0 ) {
      setShowError(true);
      setShowErrorMessage(`All invoices are settled with the entered amount.`)
      setShowErrorDesc('');
    } else {
      setShowError(false);
      setShowErrorMessage(``)
      setShowErrorDesc(``)
    }
    setFetchedInvoices(updatedInvoices);
  }

  const handlePaymentAmountChange = (e: any) => {
    applyNewPyamentAmount(e.target.value);
  };

  const onRowsSelectionHandler = (ids: any) => {
    setSelectedRows(ids);
    let totalPaymentAmount = Number(0);

    if (ids.length > 0) {
      const selectedRowsData = ids.map((id: any) => {
        const row = fetchedInvoices.find((row) => row._id == id);
        const _payment = row?.paymentAmount ? Number(row?.paymentAmount) : Number(row?.balanceAmount)
        totalPaymentAmount += _payment;
        return row
      });
      setSelectedInvoicesUpdated(selectedRowsData.filter((row: any) => row !== undefined) as AInvoice[]);
      const newPaymentAmount = (Number(totalPaymentAmount) >= Number(totalPaymentReceiptAmount)) ? totalPaymentReceiptAmount : totalPaymentAmount;
      applyNewPyamentAmount(newPaymentAmount)
    } else {
      setShowError(false);
      setSelectedInvoicesUpdated([]);
      setSelectedRows([])
      applyNewPyamentAmount(totalPaymentAmount)
    }
  };

  const TdsSelect = React.memo((row: any) => (
    <CustomSelect
      name="tdsSelected"
      id={`tesSelected_${row._id}`}
      label={`Apply Tds`}
      style={{ flex: 1, marginRight: '5px' }}
      variant="outlined"
      size="small"
      options={tdsTaxRateOptions}
      onChange={(event: SelectChangeEvent<any>) => {
        onChangeTdsHandler(event, row);
      }}
      value={row?.tdsSelected?.tdsSelected?._id || ''}
    />
  ));

  const onChangeTdsHandler = (event: any, row: any) => {
    const selectedRowId = row.row._id;
    const d = taxRateOptions.filter(function (item: any) {
      return item._id == event.target.value;
    });

    let updatedInvoices = fetchedInvoices.map((obj: any) => 
      obj._id == selectedRowId ? { ...obj, tdsSelected: {} } : obj
    );
    updatedInvoices = updatedInvoices.map((obj: any) => {
      if(obj._id == selectedRowId ) {
        var totalTaxableAmount = Number(Number(obj.principalAmount) + additionalChargesSum(obj));
        var tdsAmount = Number((d[0].taxRate * Number(obj.paymentAmount)) / 100);
        const tdsData = {
          apply: true,
          totalAmount: tdsAmount,
          taxableAmount: totalTaxableAmount,
          displayType: 0,
          tdsSelected: {
            _id: event.target.value,
            label: d[0].taxName,
            rate: d[0].taxRate
          }
        }
        return { ...obj, tdsSelected: tdsData };
      }
      return obj;
    });

    setFetchedInvoices(updatedInvoices)
  }

  //Handle date range picker
  const handleDateChange = (newValue: Date | null, name: string | undefined) => {
    if (newValue) {
      if (name === 'startDate') {
        setStartDate(newValue);
      } else if (name === 'endDate') {
        setEndDate(newValue);
      }
    }
  };

  // Define columns for the data grid
  const columns: GridColDef[] = [
    {
      field: "date",
      headerName: 'Date',
      type: "string",
      flex: 1,
      renderCell: ({ row }: GridValueGetterParams) => (
        <Typography style={{ marginLeft: '-8px' }}>
          {moment(row.date).format("DD MMM YYYY")}
        </Typography>
      )
    },
    {
      field: "payByDate",
      headerName: 'Due Date',
      type: "string",
      flex: 1,
      renderCell: ({ row }: GridValueGetterParams) => (
        <Typography style={{ marginLeft: '-8px' }}>
          {moment(row.payBydate).format("DD MMM YYYY")}
        </Typography>
      )
    },
    {
      field: "invoiceNumber",
      headerName: 'Invoice #',
      type: "string",
      flex: 1,
      renderCell: ({ row }: GridValueGetterParams) => (
        <Typography sx={{ cursor: 'pointer', fontWeight: 'bold' }}>
          {row.invoiceNumber}
        </Typography>
      )
    },
    {
      field: "amount",
      headerName: 'Total Amount',
      type: "string",
      flex: 1.2,
      renderCell: ({ row }: GridValueGetterParams) => {
        return (
        <>
        {/* {Utils.formatNumber(parseFloat(row.amount + additionalChargesSum(row)).toFixed(2) || '0')} */}
        {Utils.formatNumber(parseFloat(row.finalAmount || (row.amount + additionalChargesSum(row)).toFixed(2)) || '0')}
          {(row.balanceAmount >= 0 || row.balanceAmount <= 0) ?
            <><br />{row.status == 'PAID' ? '' : Utils.formatNumber(parseFloat(row.tempBalanceAmount) || '₹ 0', true, true) + ` (Unpaid)`} </>
            : ''}
        </>);
      }
    },
    {
      field: "status",
      headerName: 'Status',
      type: "string",
      flex: 1,
      renderCell: ({ row }: GridValueGetterParams) => (
        <Typography>
          {row.status}
        </Typography>
      )
    },
    {
      field: "paymentAmount",
      headerName: 'Amount Settled',
      type: "int",
      flex: 3,
      renderCell: ({ row }: GridValueGetterParams) => (
        <Typography>
          {row.paymentAmount}
        </Typography>
      )
    },
    {
      field: "tdsApply",
      headerName: 'Apply TDS',
      type: "string",
      flex: 1.2,
      renderCell: ({ row }: GridValueGetterParams) => (
        <TdsSelect row={row} />
      )
    }
  ];

  return (
    <Container maxWidth="xl" sx={{ mt: 1, mb: 2 }}>
      <FormProvider {...methods}>
        {/* Payment Details and Apply Section - Compact Single Row */}
        <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2}>
            {/* Left Side - Payment Details */}
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography variant="caption" color="textSecondary">Payment ID:</Typography>
                  <Typography variant="body2" fontWeight="bold">{data.payment_id}</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Typography variant="caption" color="textSecondary" sx={{ minWidth: 'fit-content' }}>Customer/Supplier:</Typography>
                  <Typography 
                    variant="body2" 
                    fontWeight="bold"
                    sx={{
                      wordWrap: 'break-word',
                      wordBreak: 'break-word',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      maxWidth: '200px'
                    }}
                    title={data.buyer}
                  >
                    {data.buyer}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography variant="caption" color="textSecondary">Payment Date:</Typography>
                  <Typography variant="body2">{moment(data.payment_date).format("DD MMM YYYY")}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography variant="caption" color="textSecondary">Total Amount:</Typography>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    {Utils.formatNumber(data.payment_amount || '0', true, true)}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Right Side - Apply Payment Controls */}
            <Grid item xs={12} md={6}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={6} sm={3}>
                  <Box>
                    <Typography variant="caption" color="textSecondary">Total Applied</Typography>
                    <Typography variant="body1" fontWeight="bold" sx={{ color: 'green' }}>
                      {Utils.formatNumber(totalAppliedAmount || 0, true, true)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box>
                    <Typography variant="caption" color="textSecondary">Total Unapplied</Typography>
                    <Typography variant="body1" fontWeight="bold" sx={{ color: 'green' }}>
                      {Utils.formatNumber((Number(totalUnappliedAmount) - Number(totalAppliedAmount)) || 0, true, true)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <CustomTextField 
                    label="Amount to Apply" 
                    name="amount"
                    type="number" 
                    variant="outlined" 
                    size="small"
                    value={amountRecevied}
                    onChange={handlePaymentAmountChange}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Button
                    sx={{ fontSize: '14px', py: 1 }}
                    variant="contained"
                    color='primary'
                    onClick={() => applyNewPyamentAmount(totalPaymentReceiptAmount)}
                    fullWidth
                    size="small"
                  >
                    Auto Apply
                  </Button>
                </Grid>
              </Grid>
            </Grid>

            {/* Status Messages */}
            {showError && (
              <Grid item xs={12}>
                <Box sx={{ 
                  p: 1, 
                  bgcolor: showErrorMessage.includes('settled') ? 'success.light' : 'warning.light', 
                  borderRadius: 1, 
                  border: '1px solid',
                  borderColor: showErrorMessage.includes('settled') ? 'success.main' : 'warning.main'
                }}>
                  <Typography 
                    variant="body2" 
                    color={showErrorMessage.includes('settled') ? 'success.dark' : 'warning.dark'} 
                    fontWeight="bold"
                  >
                    {showErrorMessage}
                  </Typography>
                  {showErrorDesc && (
                    <Typography 
                      variant="caption" 
                      color={showErrorMessage.includes('settled') ? 'success.dark' : 'warning.dark'}
                    >
                      {showErrorDesc}
                    </Typography>
                  )}
                </Box>
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Invoice List Section - Compact */}
        <Paper elevation={2} sx={{ p: 2 }}>
          {/* Filters - More Compact */}
          <Grid container spacing={1} sx={{ mb: 1 }}>
            <Grid item xs={12} sm={6} md={3}>
              <CustomSelect
                name="statuses"
                id="statuses"
                label="Invoice Status"
                variant="outlined"
                size="small"
                options={invoiceStatusOptions}
                value={selectedInvoiceStatus}
                onChange={(event: SelectChangeEvent<any>) => {
                  setSelectedInvoiceStatus(event.target.value);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DesktopDatePicker
                  label="Start Date"
                  inputFormat="dd/MM/yyyy"
                  value={startDate}
                  onChange={(value) => handleDateChange(value, 'startDate')}
                  renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DesktopDatePicker
                  label="End Date"
                  inputFormat="dd/MM/yyyy"
                  value={endDate}
                  onChange={(value) => handleDateChange(value, 'endDate')}
                  renderInput={(params) => <TextField {...params} size="small" fullWidth />}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <CustomSelect
                name="amountRange"
                id="amountRange"
                label="Amount Range"
                variant="outlined"
                size="small"
                options={amountRangeOptions}
                value={selectedAmountRange}
                onChange={(event: SelectChangeEvent<any>) => {
                  setSelectedAmountRange(event.target.value);
                }}
              />
            </Grid>
          </Grid>

          {/* Instructions - Compact */}
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption">
              Check the boxes to apply payments for multiple invoices. 
              {selectedInvoicesUpdated.length > 0 && (
                <strong> Selected: {selectedInvoicesUpdated.map(invoice => invoice.invoiceNumber).join(', ')}</strong>
              )}
            </Typography>
          </Box>

          {/* Data Grid - Optimized Height */}
          <Box sx={{ height: 'calc(100vh - 380px)', minHeight: 350, width: '100%' }}>
            <CustomDataGrid
              isRowSelectable={(params: any) =>
                params.row.status != 'DRAFT' && params.row.status != 'PAID' && params.row.status != 'OVER_PAID'
              }
              onSelectionModelChange={(ids) => onRowsSelectionHandler(ids)}
              selectionModel={memoizedSelectionModel}
              checkboxSelection={true}
              disableSelectionOnClick
              columns={columns}
              rows={fetchedInvoices}
              getRowId={(row) => row._id}
              loading={status === ApiStatus.LOADING}
            />
          </Box>

          {/* Action Buttons at Bottom */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
            <Button 
              variant="outlined" 
              onClick={() => navigate('/payments')}
              size="medium"
            >
              Cancel
            </Button>
            <LoadingButton
              type="button"
              onClick={methods.handleSubmit(onSubmit)}
              variant="contained"
              loading={status === ApiStatus.LOADING}
              size="medium"
            >
              Apply Payment
            </LoadingButton>
          </Box>
        </Paper>
      </FormProvider>
    </Container>
  );
};

export default PaymentApplication;